import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Alert,
    Badge,
    Loader,
    Select,
    Text,
    Title,
    Button,
    Switch,
    ActionIcon,
    Tooltip,
    Group,
    Paper
} from '@mantine/core';
import { IconPlus, IconCopy, IconTrash, IconX, IconLayoutDashboard, IconLayoutList } from '@tabler/icons-react';
import { LoadingState } from './LoadingState';
import { entityService } from '../../services/entityServiceSingleton';
import { debugLog } from '../../utils/debugLog';
import { EntityTableWithTabs } from './EntityTableWithTabs';
import { type TableColumn, type TableAction, type SortState, type ListResponse } from './GenericTable';
import { GenericSearch } from './GenericSearch';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { getSingularLabel, getPluralizedLabel } from '../../utils/pluralizationUtils';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import { useEntityTableLoader } from '../../hooks/useEntityTableLoader';
import { getSortByString as getSortByStringUtil } from '../../utils/sortUtils';
import { useEntityActions } from '../../hooks/useEntityActions';
import { getEntityIcon } from '../../utils/navigationIconUtils';
import { isSet, isPerson, isSpooledEmail } from '../../utils/entityTypeDetection';
import { getEntityConfig } from '../../registry';
import { DisplayNameCell } from './DisplayNameCell';
import { extractErrorMessage } from '../../utils/apiUtils';
import { GenericEntityUpsertDialog } from '../dialogs';
import { formatDateTime } from '../../utils/dateTimeUtils';
import { isIsoDateTime } from '../../utils/entityPropertyUtils';
import { copyToClipboard } from '../../utils/clipboardUtils';
import { usePerEntityTableEntitlement } from '../../hooks/usePerEntityTableEntitlement';
import { useEntityEntitlements } from '../../hooks/useEntityEntitlements';
import { isAdministrator, canCreateEntity, getBindingConstraintsFromTier2AttributeBindings, isEntitySetFacade } from '../../types/EntityEntitlements';
import EntitlementsService from '../../services/EntitlementsService';
import { getUserSettingsService } from '../../services/UserSettingsService';
import { filterRequestedAttributesByEntitlements, getDefaultRequestedAttributesForEntityType } from '../../utils/tableAttributesUtils';
import { buildAdvancedSearchUpdates, buildExplicitSimpleSearchUpdates, getRestoredAdvancedQueryState, getSearchInToStore, type SimpleSearchType } from '../../utils/searchSettingsUtils';
import { ShadcnDeleteConfirmDialog } from '../dialogs';
import { writeTableDeepLinkStateToHash } from '../../utils/tableDeepLinkUtils';
import { isAbortError } from '../../utils/abortUtils';
import { readTableDeepLinkStateFromHash } from '../../utils/tableDeepLinkUtils';
import { combineScimFiltersWithAnd, resolveSimpleSearchPlan, type SimpleSearchDialectMode } from '../../utils/simpleSearchUtils';
import { useViewModeSafe, type ViewMode } from '../../contexts/ViewModeContext';

export interface DynamicTableViewProps {
    entityType: string;
    selectedEntityId?: string | null;
    deepLinkEntityType?: string | null;
    authResolved?: boolean;
    selectedEntityType?: string;
    /** When true, do not render the internal title header (parent already renders its own). */
    hideHeader?: boolean;
    /** When true, hide create/bulk-delete toolbar actions (for embedded/specialized table surfaces). */
    hideToolbarActions?: boolean;
    /** Controls whether to show generic tabs (Schema/Journal) or only the table. */
    tabsMode?: 'full' | 'tableOnly';

    // Custom data loading function (abortSignal for cancellation; searchtext/searchAttributes for simple search when using custom loader; omitSearchDialectExpression when Expression checkbox checked)
    loadData?: (page: number, abortSignal?: AbortSignal, searchFilter?: string, selectedType?: string, sortBy?: string, searchtext?: string, searchAttributes?: string, omitSearchDialectExpression?: boolean) => Promise<{
        data: any[];
        totalResults: number;
        currentPage: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        [key: string]: any;
    }>;

    // Custom column generation
    generateColumns?: () => TableColumn<any>[];


    // Custom header content
    customHeaderContent?: React.ReactNode;

    // Entity selection handler
    onEntitySelect?: (entity: any) => void;

    /** Called when list data loads with the full list response (items + list-level $ etc.) */
    onListResponse?: (listResponse: ListResponse<any> | null) => void;

    // Search handlers
    onSearch?: (query: string) => void;
    onAdvancedSearch?: (filters: any) => void;
    onSearchQueryChange?: (query: string) => void;
    /** Deprecated: table params are parsed from hash by DynamicTableView. */
    initialSearchQuery?: string;
    /** Deprecated. */
    initialSearchDialect?: 'expression' | 'freetext';
    /** Deprecated. */
    initialAdvancedFilter?: string;
    /** Deprecated. */
    initialPage?: number;

    // External signal to force data refresh
    refreshSignal?: number;

    /** When this changes (e.g. on-behalf-of toggled), per-entity entitlements are refetched so table and Search In reflect the effective user. */
    onBehalfOfKey?: string;

    /** When set, only these attribute IDs are shown in Search In (fallback when table has not yet resolved per-entity entitlements). */
    allowedSearchAttributeIds?: string[] | null;

    // Row errors for displaying errors on specific rows
    rowErrors?: Map<string, string>;
    onClearRowError?: (rowKey: string) => void;

    /** When provided, row pencil icon is shown (when canEditRow) and clicking it opens edit directly (skips view). */
    onEditRowClick?: (entity: any) => void;

    /** When the create dialog is closed (cancel or success), called before closing so caller can e.g. update URL to list view. */
    onCloseCreateDialog?: () => void | Promise<void>;

    /** When provided, all create buttons call this instead of opening a modal dialog. Used by DynamicEntityManagement to show inline create forms. */
    onInlineCreate?: () => void;

    /**
     * Optional delete implementation used by bulk delete.
     * When omitted, defaults to ScimEntityService.deleteEntity(entityType, id).
     */
    deleteEntityById?: (id: string) => Promise<void>;

    /** When provided, replaces GenericTable with a custom renderer (e.g. card view). Flows through to GenericSearch. */
    customDataRenderer?: (props: {
        data: any[];
        isLoading: boolean;
        currentPage: number;
        totalResults: number;
        itemsPerPage: number;
        onPageChange: (page: number) => void;
        onRowClick?: (item: any) => void;
    }) => React.ReactNode;
}

/**
 * Reusable table view component that handles data loading, search, pagination, and display
 */
export const DynamicTableView: React.FC<DynamicTableViewProps> = ({
    entityType,
    selectedEntityId,
    deepLinkEntityType,
    onCloseCreateDialog,
    onInlineCreate,
    authResolved = false,
    selectedEntityType,
    hideHeader = false,
    hideToolbarActions = false,
    tabsMode = 'full',
    loadData,
    generateColumns,
    customHeaderContent,
    onEntitySelect,
    onListResponse,
    onSearch,
    onAdvancedSearch,
    onSearchQueryChange,
    initialSearchQuery: _initialSearchQuery = '',
    initialSearchDialect: _initialSearchDialect,
    initialAdvancedFilter: _initialAdvancedFilter,
    initialPage: _initialPage,
    refreshSignal,
    onBehalfOfKey,
    allowedSearchAttributeIds,
    rowErrors,
    onClearRowError,
    onEditRowClick,
    deleteEntityById,
    customDataRenderer,
}) => {
    const { isStandard, viewMode, toggleViewMode } = useViewModeSafe();

    const normalizeAttrListKey = (list: string[] | undefined | null): string => {
        if (!list || list.length === 0) return '';
        return list.map((s) => String(s || '').trim().toLowerCase()).filter(Boolean).join('|');
    };
    // Component state
    const [searchError, setSearchError] = useState<string | null>(null);
    const [userSettingsError, setUserSettingsError] = useState<string | null>(null);
    const [advFilterForPhil, setAdvFilterForPhil] = useState<string>('');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [selectedForDelete, setSelectedForDelete] = useState<any[]>([]);
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null);
    const [internalRowErrors, setInternalRowErrors] = useState<Map<string, string>>(new Map());

    const reloadRef = useRef<() => Promise<void>>(async () => { });
    const {
        allowedTableAttributeIds,
        allowedSearchAttributeIds: tier2SearchAttributeIds,
        allowedTableAttributeIdsRef,
        shouldSkipLoad: entitlementShouldSkipLoad,
        canEditRow,
        canCreateFromTier2,
        canDeleteFromTier2,
        isDisjointed,
        tier2Error
    } = usePerEntityTableEntitlement({
        entityType: entityType?.trim(),
        authResolved,
        onBehalfOfKey,
        onlyWhenEntityTypeSet: false
        // Do not pass onResolved: useEntityTableLoader already loads once when entitlementResolved flips to true; calling reload() here caused a second load and aborted the first.
    });

    /** Tier2 binding constraints (AllowedValues, RegEx) for table cell value warnings. Not available for entity-set facades (e.g. Person). */
    const tableBindingConstraintsMap = useMemo(() => {
        const tier2 = EntitlementsService.getCachedEntityEntitlementForSchemaEntity(entityType?.toLowerCase() ?? '');
        if (isEntitySetFacade(tier2)) return {};
        return getBindingConstraintsFromTier2AttributeBindings(tier2?.AttributeBindings) ?? {};
    }, [entityType, allowedTableAttributeIds]);

    const { entityEntitlements } = useEntityEntitlements();
    const showCreateButton = useMemo(() => {
        if (entityEntitlements == null) return true;
        const tier1CanCreate = canCreateEntity(entityEntitlements, entityType ?? '');
        // Tier2 (per-entity) CreateEntity should be able to RESTRICT table create even for administrators/imperator.
        // When tier2 has not resolved yet (null), fall back to tier1.
        if (canCreateFromTier2 == null) return tier1CanCreate;
        return tier1CanCreate && canCreateFromTier2 === true;
    }, [entityEntitlements, entityType, canCreateFromTier2]);

    // Entity actions integration
    const { availableActions, executeAction, actionComponent, closeAction } = useEntityActions(entityType);

    const entityConfig = getEntityConfig(entityType);
    const itemsPerPage = entityConfig?.tableFlags?.itemsPerPage ?? 25;

    const syncUrlEnabled = true;

    const updateUrlHashQueryParams = useCallback((next: {
        search?: string;
        sd?: 'expression' | 'freetext' | null | undefined;
        combine?: boolean | null | undefined;
        si?: string[] | null | undefined;
        filter?: string;
        page?: number;
    }, mode: 'replace' | 'push' = 'replace') => {
        if (!syncUrlEnabled) return;
        writeTableDeepLinkStateToHash(next, { mode });
    }, []);

    // Table deep-link params are derived from the current hash.
    const deepLink = readTableDeepLinkStateFromHash();
    const initialSimpleQuery = (deepLink.search ?? '').trim();
    const initialAdvFilter = (deepLink.filter ?? '').trim();
    const initialSearchAttributes = deepLink.si && deepLink.si.length > 0 ? deepLink.si : undefined;
    const initialCombine = deepLink.combine === true;
    const initialDialectMode: SimpleSearchDialectMode = deepLink.sd === 'expression'
        ? 'expression'
        : (deepLink.sd === 'freetext' ? 'freetext' : 'auto');
    const initialSimpleSearchPlan = resolveSimpleSearchPlan(initialSimpleQuery, initialSearchAttributes, initialDialectMode);
    const initialOmit = initialSimpleQuery
        ? initialSimpleSearchPlan.dialect === 'freetext'
        : deepLink.sd === 'freetext';
    const [currentSearchFilter, setCurrentSearchFilter] = useState<string | undefined>(initialAdvFilter ? initialAdvFilter : undefined);
    // searchText is what we actually send to the backend; initialize from URL so the first auto-load respects deep links.
    const [currentSearchText, setCurrentSearchText] = useState<string | undefined>(
        (!initialAdvFilter || initialCombine) ? initialSimpleSearchPlan.searchText : undefined
    );
    const [currentSimpleDerivedFilter, setCurrentSimpleDerivedFilter] = useState<string | undefined>(
        (!initialAdvFilter || initialCombine) ? initialSimpleSearchPlan.filter : undefined
    );
    // searchQuery is the raw input text shown in the search box.
    const [currentSearchQuery, setCurrentSearchQuery] = useState<string>(initialSimpleQuery);
    // Draft buffers: front-page simple search stays separate from explicit advanced searches.
    const [draftSimpleQuery, setDraftSimpleQuery] = useState<string>(initialSimpleQuery);
    const [draftExpressionQuery, setDraftExpressionQuery] = useState<string>(deepLink.sd === 'expression' ? initialSimpleQuery : '');
    const [draftFreetextQuery, setDraftFreetextQuery] = useState<string>(deepLink.sd === 'freetext' ? initialSimpleQuery : '');
    const [draftCustomQuery, setDraftCustomQuery] = useState<string>('');
    // Track the last *applied* simple query (what results/badge represent), independent from draft input.
    const [appliedSimpleQuery, setAppliedSimpleQuery] = useState<string>(initialSimpleQuery);
    const appliedSimpleQueryRef = useRef<string>(initialSimpleQuery);
    const [currentSearchAttributes, setCurrentSearchAttributes] = useState<string[] | undefined>(initialSearchAttributes);
    const [omitSearchDialectExpression, setOmitSearchDialectExpression] = useState<boolean>(initialOmit);
    const [appliedOmitSearchDialectExpression, setAppliedOmitSearchDialectExpression] = useState<boolean | undefined>(
        deepLink.sd === 'expression' || deepLink.sd === 'freetext' ? initialOmit : undefined
    );
    const [combinedSearchesEnabled, setCombinedSearchesEnabled] = useState<boolean>(initialCombine);
    const [currentSimpleSearchType, setCurrentSimpleSearchType] = useState<SimpleSearchType>('simple');

    // Sort state - registry-driven; entities that don't support server-side sorting set sortingDisabled: true
    const lowerEntityTypeForSort = entityType.toLowerCase();
    const isModificationErrorForSort = lowerEntityTypeForSort === 'modificationerror' || lowerEntityTypeForSort === 'modificationerrors';
    const sortingDisabledForEntity = !!(entityConfig?.tableFlags?.sortingDisabled) || isModificationErrorForSort;
    const defaultSortColumn = sortingDisabledForEntity ? null : (entityConfig?.tableFlags?.defaultSortColumn ?? 'displayname');
    const defaultSortDirection = sortingDisabledForEntity ? null : 'asc';
    const [sortState, setSortState] = useState<SortState | null>(
        sortingDisabledForEntity ? null : { column: defaultSortColumn!, direction: defaultSortDirection! }
    );

    // State for SchemaEntity dropdown
    const [localSelectedEntityType, setLocalSelectedEntityType] = useState<string>(selectedEntityType || '');
    // Set-specific filter: show system sets toggle (defaults to false = hide system sets)
    const [showSystemSets, setShowSystemSets] = useState<boolean>(false);

    // loadPage is declared later (after useEntityTableLoader). Use a ref so effects above can call it safely.
    const loadPageRef = useRef<((page: number) => void) | null>(null);
    const appliedInitialRef = useRef<string | null>(null);
    const userInitiatedSearchRef = useRef<boolean>(false);
    const hasDeepLinkedSearchRef = useRef<boolean>(false);

    // Tracks whether this view was loaded with deep-linked state (so we don't overwrite user settings on load).
    hasDeepLinkedSearchRef.current = Boolean(initialSimpleQuery || initialAdvFilter || (deepLink.page && deepLink.page > 1));

    // Get entity types for dynamic column generation and dropdowns
    const { entityTypes } = useEntityTypes();

    const deleteEntityByIdRef = useRef<((id: string) => Promise<void>) | null>(null);
    useEffect(() => {
        deleteEntityByIdRef.current = deleteEntityById ?? null;
    }, [deleteEntityById]);

    // Update local selected entity type when prop changes
    useEffect(() => {
        if (selectedEntityType !== undefined) {
            setLocalSelectedEntityType(selectedEntityType);
            // Page reset is handled by loadPage(1) in the effect below
            // Keep deep-linked / current simple search intact. The loader reads refs synchronously when it runs,
            // and table load may be deferred until entitlements resolve (shouldSkipLoad); clearing here can drop URL search.
            const adv = currentSearchFilterRef.current;
            const simple = (currentSearchQueryRef.current || '').trim();
            const resolvedPlan = resolveSimpleSearchPlan(
                simple,
                currentSearchAttributesRef.current,
                appliedOmitSearchDialectExpressionRef.current === undefined
                    ? 'auto'
                    : (appliedOmitSearchDialectExpressionRef.current ? 'freetext' : 'expression')
            );
            const shouldKeepSimpleSearch = !adv || combinedSearchesEnabledRef.current;
            setCurrentSearchText(shouldKeepSimpleSearch ? resolvedPlan.searchText : undefined);
            currentSearchTextRef.current = shouldKeepSimpleSearch ? resolvedPlan.searchText : undefined;
            setCurrentSimpleDerivedFilter(shouldKeepSimpleSearch ? resolvedPlan.filter : undefined);
            currentSimpleDerivedFilterRef.current = shouldKeepSimpleSearch ? resolvedPlan.filter : undefined;
        }
    }, [selectedEntityType]);


    // Reset sort state when entity type changes to ensure valid sort column
    useEffect(() => {
        const lowerEntityType = entityType.toLowerCase();
        const isJournalEntry = lowerEntityType === 'journalentry' ||
            lowerEntityType === 'journal entry' ||
            lowerEntityType.replace(/\s+/g, '') === 'journalentry';

        // Journal entries should not use sorting
        if (isJournalEntry) {
            setSortState(null);
        } else {
            setSortState({ column: 'displayname', direction: 'asc' });
        }
        // Page reset is handled by useEntityTableLoader when entityType changes
    }, [entityType]);

    // Scroll to top will be set up after useEntityTableLoader declares currentPage

    // Default data loading function
    const defaultLoadData = useCallback(async (page: number = 1, searchFilter?: string, _selectedType?: string, overrideShowSystemSets?: boolean, sortBy?: string, abortSignal?: AbortSignal, searchtext?: string, searchAttributes?: string, omitSearchDialectExpression?: boolean) => {
        if (!authResolved) {
            // Reduced logging
            return {
                data: [],
                totalResults: 0,
                currentPage: 1,
                totalPages: 1,
                hasNextPage: false,
                hasPreviousPage: false
            };
        }

        // Loading state is managed by useEntityTableLoader

        try {
            let response;

            const normalizedEntityType = entityType.toLowerCase().replace(/\s+/g, '');
            const isJournalEntry = normalizedEntityType === 'journalentry';

            // Centralized: requested attributes filtered by tier2 entitlements (do not call API when allowed is null)
            const allowed = allowedTableAttributeIdsRef.current;
            const requestedAttributes = getDefaultRequestedAttributesForEntityType(normalizedEntityType);
            const effectiveAttributes = filterRequestedAttributesByEntitlements(requestedAttributes, allowed, { isJournalEntry });

            // Build effective filter including showSystemSets if applicable
            let effectiveFilter: string | undefined = searchFilter;
            if (normalizedEntityType === 'set') {
                // Use the override value if provided, otherwise use state
                const shouldShowSystemSets = overrideShowSystemSets !== undefined ? overrideShowSystemSets : showSystemSets;
                if (!shouldShowSystemSets) {
                    // When NOT showing system sets (toggle OFF), use "IsSystem eq false" filter
                    const sysFilter = 'IsSystem eq false';
                    effectiveFilter = effectiveFilter ? `(${effectiveFilter}) and (${sysFilter})` : sysFilter;
                } else {
                    // When showing system sets (toggle ON), use no system filter - only use searchFilter
                    // effectiveFilter already set to searchFilter above, so no change needed
                }

                // Log set fetch details on a single line
                debugLog(`🔍 Sets Fetch: toggle="${shouldShowSystemSets ? 'ON (show system sets)' : 'OFF (hide system sets)'}" showSystemSets=${shouldShowSystemSets} searchFilter="${searchFilter || 'none'}" effectiveFilter="${effectiveFilter || 'none'}"`);
            }

            // Add TargetType filter for Journal Entry when localSelectedEntityType is set
            if (isJournalEntry && _selectedType) {
                const targetTypeFilter = `TargetType eq "${_selectedType}"`;
                effectiveFilter = effectiveFilter ? `(${effectiveFilter}) and (${targetTypeFilter})` : targetTypeFilter;
            }
            // Normalize empty/whitespace filters to undefined
            if (!effectiveFilter || (typeof effectiveFilter === 'string' && effectiveFilter.trim() === '')) {
                effectiveFilter = undefined;
            }

            // Disjointed entities (JournalEntry, ModificationError, SpooledEmail) do not support sorting
            const isModificationError = normalizedEntityType === 'modificationerror' || normalizedEntityType === 'modificationerrors';
            const isSpooledEmailType = normalizedEntityType === 'spooledemail';
            const effectiveSortBy = (isJournalEntry || isModificationError || isSpooledEmailType) ? undefined : sortBy;

            response = await entityService.getEntitiesByType(
                normalizedEntityType,
                page,
                itemsPerPage,
                effectiveSortBy, // Journal entries should not send sort
                effectiveFilter,
                effectiveAttributes, // Use journal entry specific attributes if needed
                abortSignal,
                searchtext,
                searchAttributes,
                undefined, // onBehalfOfUpn
                undefined, // countOnly
                omitSearchDialectExpression
            );

            // Reduced logging - only log errors or important info

            const rows = response.items ?? response.data;
            if (rows && Array.isArray(rows)) {
                return {
                    ...response,
                    data: rows,
                    items: rows,
                    totalResults: response.totalResults ?? rows.length,
                    currentPage: page,
                    totalPages: Math.ceil((response.totalResults ?? rows.length) / itemsPerPage),
                    hasNextPage: response.hasNextPage ?? false,
                    hasPreviousPage: response.hasPreviousPage ?? false
                };
            } else {
                console.warn(`📋 DynamicTableView: No data in ${entityType} response`);
                console.warn(`📋 DynamicTableView: Response structure:`, {
                    hasData: !!rows,
                    dataType: typeof rows,
                    isArray: Array.isArray(rows),
                    dataKeys: response ? Object.keys(response) : 'N/A'
                });
                return {
                    ...response,
                    data: [],
                    items: [],
                    totalResults: 0,
                    currentPage: 1,
                    totalPages: 1,
                    hasNextPage: false,
                    hasPreviousPage: false
                };
            }
        } catch (err: any) {
            // Ignore abort errors - they're expected when navigating away
            if (isAbortError(err, abortSignal)) {
                return {
                    data: [],
                    totalResults: 0,
                    currentPage: 1,
                    totalPages: 1,
                    hasNextPage: false,
                    hasPreviousPage: false
                };
            }
            console.error(`❌ DynamicTableView: Error loading ${entityType} entities:`, err);
            // Don't set error here - let loadEntities handle it based on whether it's a search
            // Re-throw so loadEntities can catch and handle appropriately
            throw err;
        } finally {
            // Loading state is managed by useEntityTableLoader
        }
    }, [entityService, entityType, authResolved, itemsPerPage, showSystemSets]); // allowedTableAttributeIdsRef is a ref, not a dependency

    // Convert sortState to sortBy string format for API (using centralized utility)
    const getSortByString = useCallback((sort: SortState | null): string | undefined => {
        return getSortByStringUtil(sort, entityType);
    }, [entityType]);

    // Use refs to track current state for the wrapper function
    const currentSearchFilterRef = useRef(currentSearchFilter);
    const currentSearchTextRef = useRef(currentSearchText);
    const currentSimpleDerivedFilterRef = useRef(currentSimpleDerivedFilter);
    const currentSearchQueryRef = useRef(currentSearchQuery);
    const draftSimpleQueryRef = useRef<string>(draftSimpleQuery);
    const draftExpressionQueryRef = useRef<string>(draftExpressionQuery);
    const draftFreetextQueryRef = useRef<string>(draftFreetextQuery);
    const draftCustomQueryRef = useRef<string>(draftCustomQuery);
    const previousEntityTypeRef = useRef(entityType);
    const currentSearchAttributesRef = useRef<string[] | undefined>(currentSearchAttributes);
    const currentOmitSearchDialectExpressionRef = useRef<boolean>(omitSearchDialectExpression);
    const appliedOmitSearchDialectExpressionRef = useRef<boolean | undefined>(appliedOmitSearchDialectExpression);
    const combinedSearchesEnabledRef = useRef<boolean>(combinedSearchesEnabled);
    const currentSimpleSearchTypeRef = useRef<SimpleSearchType>(currentSimpleSearchType);
    const sortStateRef = useRef(sortState);
    const showSystemSetsRef = useRef(showSystemSets);
    const localSelectedEntityTypeRef = useRef(localSelectedEntityType);
    const loadDataRef = useRef(loadData);
    const defaultLoadDataRef = useRef(defaultLoadData);

    // When entity type changes (e.g. workflow -> set), clear filters and search so the new table shows unfiltered
    if (previousEntityTypeRef.current !== entityType) {
        previousEntityTypeRef.current = entityType;
        currentSearchFilterRef.current = undefined;
        currentSearchTextRef.current = undefined;
        currentSimpleDerivedFilterRef.current = undefined;
    }
    const lastAppliedHashRef = useRef<string>('');
    useEffect(() => {
        const applyFromHash = () => {
            // Apply deep link state only when the hash actually changes (prevents clobbering user edits and avoids loops).
            const rawHash = typeof window !== 'undefined' ? (window.location.hash || '') : '';
            if (rawHash === lastAppliedHashRef.current) return;
            lastAppliedHashRef.current = rawHash;

            const link = readTableDeepLinkStateFromHash();
            const adv = (link.filter ?? '').trim();
            const simple = (link.search ?? '').trim();
            const shouldCombine = link.combine === true;
            const searchPlan = resolveSimpleSearchPlan(
                simple,
                currentSearchAttributesRef.current,
                link.sd === 'expression' ? 'expression' : (link.sd === 'freetext' ? 'freetext' : 'auto')
            );
            const nextOmit = simple ? searchPlan.dialect === 'freetext' : link.sd === 'freetext';

            setCurrentSearchFilter(adv ? adv : undefined);
            setCurrentSearchText((!adv || shouldCombine) ? searchPlan.searchText : undefined);
            setCurrentSimpleDerivedFilter((!adv || shouldCombine) ? searchPlan.filter : undefined);
            setCurrentSearchQuery(simple);
            setDraftSimpleQuery(simple);
            draftSimpleQueryRef.current = simple;
            setAdvFilterForPhil('');

            // Restore dialect from deep link when a simple search is present; otherwise keep current setting.
            if (simple && currentOmitSearchDialectExpressionRef.current !== nextOmit) {
                setOmitSearchDialectExpression(nextOmit);
                currentOmitSearchDialectExpressionRef.current = nextOmit;
            }
            setAppliedOmitSearchDialectExpression(link.sd === 'expression' || link.sd === 'freetext' ? nextOmit : undefined);
            appliedOmitSearchDialectExpressionRef.current = link.sd === 'expression' || link.sd === 'freetext' ? nextOmit : undefined;

            // Restore Search In from deep link (only when it differs).
            if (link.si && link.si.length > 0) {
                const nextKey = normalizeAttrListKey(link.si);
                const curKey = normalizeAttrListKey(currentSearchAttributesRef.current);
                if (nextKey !== curKey) {
                    setCurrentSearchAttributes(link.si);
                    currentSearchAttributesRef.current = link.si;
                }
            }

            // Restore Combine from deep link.
            if (link.combine === true && combinedSearchesEnabledRef.current !== true) {
                setCombinedSearchesEnabled(true);
                combinedSearchesEnabledRef.current = true;
            }

            // Keep refs in sync for the loader's initial call (effects may run in either order).
            currentSearchFilterRef.current = adv ? adv : undefined;
            currentSearchQueryRef.current = simple;
            currentSearchTextRef.current = (!adv || shouldCombine) ? searchPlan.searchText : undefined;
            currentSimpleDerivedFilterRef.current = (!adv || shouldCombine) ? searchPlan.filter : undefined;
        };

        applyFromHash();
        window.addEventListener('hashchange', applyFromHash);
        return () => window.removeEventListener('hashchange', applyFromHash);
        // entityType is included so a new table instance re-applies current hash.
    }, [entityType]);

    // Ref to ignore stale user-settings responses when entity type changes before fetch completes
    const userSettingsFetchEntityTypeRef = useRef<string | null>(null);

    // Front-page simple search should start blank unless a deep link provides search state.
    // Saved searches are restored only when the user opens Advanced.
    useEffect(() => {
        if (!authResolved || !entityType?.trim()) return;
        // If the URL deep-linked a search/filter, prefer it over user-settings restore.
        const link = readTableDeepLinkStateFromHash();
        if ((link.filter ?? '').trim() || (link.search ?? '').trim() || (link.page ?? null)) {
            return;
        }
        setDraftSimpleQuery('');
        draftSimpleQueryRef.current = '';
        setCurrentSearchQuery('');
        currentSearchQueryRef.current = '';
        return () => {
            userSettingsFetchEntityTypeRef.current = null;
        };
    }, [entityType, authResolved]);

    // Update refs when values change
    useEffect(() => { currentSearchFilterRef.current = currentSearchFilter; }, [currentSearchFilter]);
    useEffect(() => { currentSearchTextRef.current = currentSearchText; }, [currentSearchText]);
    useEffect(() => { currentSimpleDerivedFilterRef.current = currentSimpleDerivedFilter; }, [currentSimpleDerivedFilter]);
    useEffect(() => { currentSearchQueryRef.current = currentSearchQuery; }, [currentSearchQuery]);
    useEffect(() => { draftSimpleQueryRef.current = draftSimpleQuery; }, [draftSimpleQuery]);
    useEffect(() => { draftExpressionQueryRef.current = draftExpressionQuery; }, [draftExpressionQuery]);
    useEffect(() => { draftFreetextQueryRef.current = draftFreetextQuery; }, [draftFreetextQuery]);
    useEffect(() => { draftCustomQueryRef.current = draftCustomQuery; }, [draftCustomQuery]);
    useEffect(() => { appliedSimpleQueryRef.current = appliedSimpleQuery; }, [appliedSimpleQuery]);
    // Badge should represent the last applied search (not draft input).
    const appliedSimpleQueryForUi = ((appliedSimpleQuery ?? '').trim() || undefined);

    useEffect(() => { currentSearchAttributesRef.current = currentSearchAttributes; }, [currentSearchAttributes]);
    useEffect(() => { currentOmitSearchDialectExpressionRef.current = omitSearchDialectExpression; }, [omitSearchDialectExpression]);
    useEffect(() => { appliedOmitSearchDialectExpressionRef.current = appliedOmitSearchDialectExpression; }, [appliedOmitSearchDialectExpression]);
    useEffect(() => { combinedSearchesEnabledRef.current = combinedSearchesEnabled; }, [combinedSearchesEnabled]);
    useEffect(() => { currentSimpleSearchTypeRef.current = currentSimpleSearchType; }, [currentSimpleSearchType]);
    useEffect(() => { sortStateRef.current = sortState; }, [sortState]);
    useEffect(() => { showSystemSetsRef.current = showSystemSets; }, [showSystemSets]);
    useEffect(() => { localSelectedEntityTypeRef.current = localSelectedEntityType; }, [localSelectedEntityType]);
    useEffect(() => { loadDataRef.current = loadData; }, [loadData]);
    useEffect(() => { defaultLoadDataRef.current = defaultLoadData; }, [defaultLoadData]);
    useEffect(() => { allowedTableAttributeIdsRef.current = allowedTableAttributeIds; }, [allowedTableAttributeIds]);

    // Wrapper function for useEntityTableLoader that adapts DynamicTableView's requirements
    const loadDataForHook = useCallback(async (page: number = 1, abortSignal?: AbortSignal) => {
        const searchFilter = combineScimFiltersWithAnd(currentSearchFilterRef.current, currentSimpleDerivedFilterRef.current);
        const searchtext = currentSearchTextRef.current;
        const sortBy = getSortByString(sortStateRef.current);
        const overrideShowSystemSets = showSystemSetsRef.current;
        const selectedType = localSelectedEntityTypeRef.current;

        // If user checked anything beyond DisplayName, pass searchAttributes through to backend.
        const attrs = currentSearchAttributesRef.current;
        const shouldSendSearchAttributes = !!(attrs && attrs.some(a => a?.toLowerCase() !== 'displayname'));
        const searchAttributes = shouldSendSearchAttributes ? attrs!.join(',') : undefined;
        const omitSearchDialectExpression = currentOmitSearchDialectExpressionRef.current;

        // Debug deep-link + entitlement-defer issues: log the request inputs for the first page.
        // Use debugLog so it shows up alongside existing app logs.
        const link = readTableDeepLinkStateFromHash();
        if (page === 1 && ((link.search ?? '').trim() || (link.filter ?? '').trim() || (link.page && link.page > 1))) {
            debugLog('🔍 DynamicTableView: load inputs (deep-link)', {
                entityType,
                initialSearchQuery: link.search,
                initialAdvancedFilter: link.filter,
                initialPage: link.page,
                ref_searchText: searchtext,
                ref_filter: searchFilter,
                ref_query: currentSearchQueryRef.current,
                ref_searchAttributes: currentSearchAttributesRef.current,
                shouldSkipLoad: entitlementShouldSkipLoad?.() ?? undefined,
            });
        }

        try {
            let result;
            if (loadDataRef.current) {
                result = await loadDataRef.current(page, abortSignal, searchFilter, selectedType, sortBy, searchtext, searchAttributes, omitSearchDialectExpression);
            } else {
                result = await defaultLoadDataRef.current(page, searchFilter, selectedType, overrideShowSystemSets, sortBy, abortSignal, searchtext, searchAttributes, omitSearchDialectExpression);
            }

            // Clear search error on successful load if this was a search
            if (searchFilter) {
                setSearchError(null);
            }

            // Clear any previous user-settings error so the 200 success path takes precedence
            setUserSettingsError(null);

            // On successful response (200), persist simple search to user settings so it can be restored.
            // If the save fails, show the error in the UI after the success path has run (setTimeout so we
            // don't overwrite or get overwritten by any error-clear from the 200).
            try {
                const rows = (result as any)?.items ?? (result as any)?.data;
                const hasAtLeastOneRecord = Array.isArray(rows) && rows.length > 0;
                if (!hasAtLeastOneRecord) {
                    return result;
                }
                // If the URL provided a deep-linked search/filter/page and the user has not interacted yet,
                // don't overwrite their saved search preferences just because they opened a shared link.
                if (hasDeepLinkedSearchRef.current && !userInitiatedSearchRef.current) {
                    return result;
                }
                const attrsForStorage = currentSearchAttributesRef.current;
                const searchInToStore = getSearchInToStore(attrsForStorage);
                const setSearchErr = (err: unknown) => {
                    setTimeout(() => {
                        setUserSettingsError(extractErrorMessage(err) || 'Could not save search preference');
                    }, 0);
                };
                const simpleToStore = (appliedSimpleQueryRef.current || '').trim();
                const appliedDialectOmit = appliedOmitSearchDialectExpressionRef.current;
                if (simpleToStore !== '' && appliedDialectOmit !== undefined) {
                    const updates = buildExplicitSimpleSearchUpdates({
                        dialect: appliedDialectOmit ? 'freetext' : 'expression',
                        query: simpleToStore,
                        searchIn: searchInToStore
                    });
                    getUserSettingsService().setSearch(entityType, updates, true).catch(setSearchErr);
                } else if (searchInToStore && searchInToStore.length > 0) {
                    getUserSettingsService().setSearch(entityType, { searchIn: searchInToStore }, true).catch(setSearchErr);
                }
            } catch {
                /* key validation or other; don't fail the load */
            }

            return result;
        } catch (error: any) {
            // Ignore abort errors
            if (isAbortError(error)) {
                throw error; // Re-throw to let hook handle it
            }

            // Extract error message using utility function
            let errorMessage = extractErrorMessage(error);

            // Only prepend "not yet available" for generic 400s (no specific API message). When the API
            // returns a specific error (e.g. "The 'present' operator is not supported"), show that as-is —
            // the entity type may be available (e.g. disjointed/facade) and the failure is for another reason.
            const isGeneric400 = !errorMessage || errorMessage === 'API request failed' ||
                /^400:\s*Bad Request$/i.test(errorMessage) || errorMessage === 'Bad Request';
            if (error?.response?.status === 400 && isGeneric400) {
                errorMessage = `${entityType} entity type exists in schema but is not yet available in the API. ${errorMessage || '400 Bad Request'}`;
            }
            if (!errorMessage || errorMessage === 'API request failed') {
                // Fallback to generic message if extraction didn't work
                errorMessage = `Failed to load ${entityType} entities`;
            }

            // Set error in appropriate location based on whether this was a search
            if (searchFilter) {
                // Search error - display only in GenericSearch; return empty so hook does not set error (avoids duplicate banner)
                setSearchError(errorMessage);
                return {
                    data: [],
                    items: [],
                    totalResults: 0,
                    currentPage: 1,
                    totalPages: 1,
                    hasNextPage: false,
                    hasPreviousPage: false
                };
            }

            // Initial/default load error - re-throw so hook sets error and clears data
            throw error;
        }
    }, [getSortByString, entityType]);

    // Use centralized hook for data loading
    const {
        data: entities,
        listResponse,
        loading,
        error,
        currentPage,
        totalPages,
        totalResults,
        hasNextPage,
        hasPreviousPage,
        setCurrentPage,
        setError,
        reload,
        loadPage
    } = useEntityTableLoader<any>({
        entityType,
        authResolved,
        loadData: loadDataForHook,
        initialPage: 1,
        itemsPerPage,
        clearDataOnEntityTypeChange: true,
        autoLoad: true,
        shouldSkipLoad: entitlementShouldSkipLoad,
        entitlementResolved: allowedTableAttributeIds !== null,
        refreshKey: onBehalfOfKey
    });

    useEffect(() => { loadPageRef.current = loadPage; }, [loadPage]);
    useEffect(() => { reloadRef.current = reload; }, [reload]);

    // Apply initial deep-linked search/filter/page once per entityType after loadPage is available.
    useEffect(() => {
        if (!authResolved) return;
        const doLoad = loadPageRef.current;
        if (!doLoad) return;
        const link = readTableDeepLinkStateFromHash();
        const key = `${entityType.toLowerCase()}::${link.search || ''}::${link.filter || ''}::${link.page || ''}::${link.sd || ''}::${link.combine ? '1' : '0'}::${(link.si || []).join(',')}`;
        if (appliedInitialRef.current === key) return;
        appliedInitialRef.current = key;

        if ((link.search ?? '').trim() || (link.filter ?? '').trim() || (link.page && link.page > 1)) {
            debugLog('🔍 DynamicTableView: applying deep-link state', {
                entityType,
                initialSearchQuery: link.search,
                initialAdvancedFilter: link.filter,
                initialPage: link.page,
                sd: link.sd,
                combine: link.combine,
                si: link.si
            });
        }

        const page = link.page && link.page > 0 ? link.page : 1;
        const adv = (link.filter ?? '').trim();
        const simple = (link.search ?? '').trim();
        const shouldCombine = link.combine === true;
        const searchPlan = resolveSimpleSearchPlan(
            simple,
            currentSearchAttributes,
            link.sd === 'expression' ? 'expression' : (link.sd === 'freetext' ? 'freetext' : 'auto')
        );

        if (adv) {
            setCurrentSearchFilter(adv);
            setCurrentSearchText(shouldCombine ? searchPlan.searchText : undefined);
            setCurrentSimpleDerivedFilter(shouldCombine ? searchPlan.filter : undefined);
            setCurrentSearchQuery(simple);
            setAppliedSimpleQuery(simple);
            appliedSimpleQueryRef.current = simple;
            // Update refs synchronously so the next load sees the deep-linked filter immediately.
            currentSearchFilterRef.current = adv;
            currentSearchTextRef.current = shouldCombine ? searchPlan.searchText : undefined;
            currentSimpleDerivedFilterRef.current = shouldCombine ? searchPlan.filter : undefined;
            setTimeout(() => doLoad(page), 0);
            return;
        }
        if (simple) {
            setCurrentSearchQuery(simple);
            setAppliedSimpleQuery(simple);
            appliedSimpleQueryRef.current = simple;
            setCurrentSearchText(searchPlan.searchText);
            setCurrentSimpleDerivedFilter(searchPlan.filter);
            setCurrentSearchFilter(undefined);
            // Update refs synchronously so the next load sees the deep-linked search immediately.
            currentSearchQueryRef.current = simple;
            currentSearchFilterRef.current = undefined;
            currentSearchTextRef.current = searchPlan.searchText;
            currentSimpleDerivedFilterRef.current = searchPlan.filter;
            setTimeout(() => doLoad(page), 0);
            return;
        }
        if (page !== 1) {
            setTimeout(() => doLoad(page), 0);
        }
    }, [authResolved, entityType, currentSearchAttributes]);

    // Clear selection and bulk-delete state on entityType change
    useEffect(() => {
        setSelectedForDelete([]);
        setBulkDeleteDialogOpen(false);
        setIsBulkDeleting(false);
        setBulkDeleteError(null);
        setInternalRowErrors(new Map());
    }, [entityType]);

    // Scroll to top when component mounts or entity type changes
    useScrollToTop([entityType, currentPage]);

    // Track if we're currently handling an advanced search to prevent useEffect from aborting it
    const isHandlingAdvancedSearchRef = React.useRef<boolean>(false);

    // Notify parent of list response (items + list-level $ etc.) for detail $ tab
    useEffect(() => {
        if (onListResponse) {
            onListResponse(listResponse);
        }
    }, [listResponse, onListResponse]);

    // Refresh when external signal changes (not on initial mount – main loader effect handles first load)
    const lastRefreshSignalRef = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (!authResolved) return;
        if (refreshSignal === undefined) return;
        // Don't reload if we're currently handling an advanced search
        if (isHandlingAdvancedSearchRef.current) return;
        const prev = lastRefreshSignalRef.current;
        lastRefreshSignalRef.current = refreshSignal;
        if (prev !== undefined && prev !== refreshSignal) {
            loadPage(1);
        }
    }, [refreshSignal, authResolved, loadPage]);

    // Load entities when localSelectedEntityType changes (for Journal Entry TargetType filtering)
    useEffect(() => {
        if (!authResolved) return;
        // Don't reload if we're currently handling an advanced search
        if (isHandlingAdvancedSearchRef.current) return;

        const normalizedEntityType = entityType.toLowerCase();
        const isJournalEntry = normalizedEntityType === 'journalentry' ||
            normalizedEntityType === 'journal entry' ||
            normalizedEntityType.replace(/\s+/g, '') === 'journalentry';

        // For Journal Entry, reload when localSelectedEntityType changes (filters by TargetType)
        // For schemaentity with custom loadData, the loadData function handles it
        if (isJournalEntry || (loadDataRef.current && localSelectedEntityType)) {
            loadPage(1);
        }
    }, [localSelectedEntityType, authResolved, entityType, loadPage]);

    // Handle entity selection
    const handleEntitySelect = useCallback((entityId: string) => {
        const entity = entities.find(e => {
            const eId = e.Id || e.id;
            return eId === entityId || String(eId) === String(entityId);
        });
        if (entity && onEntitySelect) {
            // If entity has rawData that is an object, use that (it has the correct structure)
            // Otherwise use the entity as-is
            const entityToPass = (entity.rawData && typeof entity.rawData === 'object' && !Array.isArray(entity.rawData))
                ? entity.rawData
                : entity;
            onEntitySelect(entityToPass);
        } else if (onEntitySelect) {
            // Entity not found in current page - pass ID string to parent to fetch
            debugLog(`🔍 DynamicTableView: Entity ${entityId} not found in current page data, passing to parent to fetch`);
            onEntitySelect(entityId);
        }
    }, [entities, onEntitySelect]);

    // Handle deep linking to specific entity
    const lastProcessedEntityIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (selectedEntityId && deepLinkEntityType && authResolved) {
            // Prevent duplicate processing of the same entity ID
            if (lastProcessedEntityIdRef.current === selectedEntityId) {
                return;
            }
            lastProcessedEntityIdRef.current = selectedEntityId;
            // Reduced logging
            handleEntitySelect(selectedEntityId);
        } else if (!selectedEntityId) {
            // Clear the ref when selectedEntityId is cleared
            lastProcessedEntityIdRef.current = null;
        }
    }, [selectedEntityId, deepLinkEntityType, entityType, authResolved, handleEntitySelect]);

    // Handle sort change
    const handleSortChange = useCallback((newSortState: SortState | null) => {
        // Keep ref in sync immediately so loadPage uses the newly selected sort on first click.
        sortStateRef.current = newSortState;
        setSortState(newSortState);
        // Reload data with new sort - hook will use updated sort state via ref
        loadPage(1);
    }, [loadPage]);

    // Handle page changes
    const handlePageChange = useCallback((page: number) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const rawSearch = (currentSearchQueryRef.current ?? '').trim();
        updateUrlHashQueryParams({
            // Keep URL "search" as raw query text (not processed searchText) so reload preserves what user typed.
            search: rawSearch,
            sd: rawSearch
                ? (appliedOmitSearchDialectExpressionRef.current === undefined
                    ? undefined
                    : (appliedOmitSearchDialectExpressionRef.current ? 'freetext' : 'expression'))
                : undefined,
            combine: rawSearch ? (combinedSearchesEnabledRef.current ? true : undefined) : undefined,
            si: rawSearch ? (getSearchInToStore(currentSearchAttributesRef.current) ?? undefined) : undefined,
            filter: currentSearchFilterRef.current ?? '',
            page,
        }, 'push');
        loadPage(page);
    }, [loadPage, updateUrlHashQueryParams]);

    const bulkDeleteEnabled = canDeleteFromTier2 === true;

    const effectiveRowErrors = useMemo(() => {
        if (!rowErrors && internalRowErrors.size === 0) return undefined;
        const merged = new Map<string, string>();
        if (rowErrors) {
            for (const [k, v] of rowErrors.entries()) merged.set(k, v);
        }
        for (const [k, v] of internalRowErrors.entries()) merged.set(k, v);
        return merged;
    }, [rowErrors, internalRowErrors]);

    const handleClearEffectiveRowError = useCallback((rowKey: string) => {
        setInternalRowErrors(prev => {
            if (!prev.has(rowKey)) return prev;
            const next = new Map(prev);
            next.delete(rowKey);
            return next;
        });
        onClearRowError?.(rowKey);
    }, [onClearRowError]);

    const getEntityId = useCallback((entity: any): string => {
        const entityId = entity?.Id ?? entity?.id;
        const idString = typeof entityId === 'string' ? entityId :
            (entityId && typeof entityId === 'object' && entityId.value) ? entityId.value :
                (entityId && typeof entityId === 'object' && entityId.Id) ? entityId.Id :
                    String(entityId);
        return idString;
    }, []);

    const handleConfirmBulkDelete = useCallback(async () => {
        if (!bulkDeleteEnabled) return;
        const items = selectedForDelete;
        if (!items || items.length === 0) return;
        setIsBulkDeleting(true);
        setBulkDeleteError(null);
        const nextRowErrors = new Map<string, string>();
        let successCount = 0;
        try {
            for (const item of items) {
                const id = getEntityId(item);
                try {
                    const del = deleteEntityByIdRef.current;
                    if (del) await del(id);
                    else await entityService.deleteEntity(entityType, id);
                    successCount++;
                } catch (err) {
                    nextRowErrors.set(id, extractErrorMessage(err) || 'Delete failed');
                }
            }
        } finally {
            setInternalRowErrors(prev => {
                const merged = new Map(prev);
                for (const [k, v] of nextRowErrors.entries()) merged.set(k, v);
                return merged;
            });
            setIsBulkDeleting(false);
            setBulkDeleteDialogOpen(false);
            // Clear selection for successful deletes; keep failed items selected so user can retry if desired
            if (successCount > 0) {
                setSelectedForDelete(prev => prev.filter(item => nextRowErrors.has(getEntityId(item))));
                reload();
            }
            if (nextRowErrors.size > 0) {
                setBulkDeleteError(`${nextRowErrors.size} item(s) failed to delete`);
            }
        }
    }, [bulkDeleteEnabled, selectedForDelete, entityService, entityType, getEntityId, reload]);

    // Handle search (simple search; combines with existing advanced filter when both are applied)
    const handleSimpleSearch = useCallback((query: string, searchAttributes?: string[], expressionDialectOmitted?: boolean | null, searchType?: 'simple' | 'simpleCustom') => {
        userInitiatedSearchRef.current = true;
        const explicitDialectMode: SimpleSearchDialectMode =
            expressionDialectOmitted === true ? 'freetext' :
                (expressionDialectOmitted === false ? 'expression' : 'auto');
        const resolvedSearchType = searchType ?? 'simple';
        setCurrentSimpleSearchType(resolvedSearchType);
        const trimmedQuery = query.trim();
        const resolvedPlan = resolveSimpleSearchPlan(trimmedQuery, searchAttributes, explicitDialectMode);
        const resolvedOmit = resolvedPlan.dialect === 'freetext';
        currentOmitSearchDialectExpressionRef.current = resolvedOmit;
        setOmitSearchDialectExpression(resolvedOmit);
        const appliedDialectOmit = expressionDialectOmitted === undefined || expressionDialectOmitted === null
            ? undefined
            : expressionDialectOmitted;
        setAppliedOmitSearchDialectExpression(appliedDialectOmit);
        appliedOmitSearchDialectExpressionRef.current = appliedDialectOmit;
        setCurrentSearchText(resolvedPlan.searchText);
        currentSearchTextRef.current = resolvedPlan.searchText;
        setCurrentSimpleDerivedFilter(resolvedPlan.filter);
        currentSimpleDerivedFilterRef.current = resolvedPlan.filter;
        setCurrentSearchQuery(trimmedQuery); // Keep search box in sync with applied value so re-submit works
        setDraftSimpleQuery(trimmedQuery);
        draftSimpleQueryRef.current = trimmedQuery;
        setAppliedSimpleQuery(trimmedQuery);
        appliedSimpleQueryRef.current = trimmedQuery;
        currentSearchQueryRef.current = trimmedQuery;
        currentSearchAttributesRef.current = searchAttributes;
        setCurrentSearchAttributes(searchAttributes);
        if (explicitDialectMode === 'expression') {
            setDraftExpressionQuery(trimmedQuery);
            draftExpressionQueryRef.current = trimmedQuery;
        } else if (explicitDialectMode === 'freetext') {
            setDraftFreetextQuery(trimmedQuery);
            draftFreetextQueryRef.current = trimmedQuery;
        }

        // If combining is disabled, clear any active advanced filter so users don't end up with multiple badges unknowingly.
        if (!combinedSearchesEnabledRef.current) {
            setCurrentSearchFilter(undefined);
            currentSearchFilterRef.current = undefined;
        }

        loadPage(1);

        updateUrlHashQueryParams({
            // Keep URL "search" as raw query text; we derive processed searchText at runtime.
            search: trimmedQuery,
            sd: trimmedQuery
                ? (appliedDialectOmit === undefined ? undefined : (appliedDialectOmit ? 'freetext' : 'expression'))
                : undefined,
            combine: trimmedQuery ? (combinedSearchesEnabledRef.current ? true : undefined) : undefined,
            si: trimmedQuery ? (getSearchInToStore(currentSearchAttributesRef.current) ?? undefined) : undefined,
            filter: currentSearchFilterRef.current ?? '',
            page: 1,
        }, 'push');

        if (onSearch) {
            onSearch(query);
        }
    }, [onSearch, loadPage, updateUrlHashQueryParams]);

    // Handle advanced search (combines with existing simple search when both are applied)
    const handleAdvancedSearch = useCallback((filters: any) => {
        userInitiatedSearchRef.current = true;
        const filterString = typeof filters === 'string'
            ? filters
            : (filters?.filter || filters?.query || '');
        // When applying from Phil, caller may pass current simple search so we combine correctly
        const explicitSearchText = filters?.searchtext ?? filters?.searchText ?? currentSearchTextRef.current;
        const explicitSearchAttributes = filters?.searchAttributes ?? filters?.searchattributes;
        const isCustomSimpleFilter = filters?.isCustomSimpleFilter === true;
        const urlSearchQuery = isCustomSimpleFilter ? '' : ((currentSearchQueryRef.current ?? '').trim());

        debugLog('🔍 DynamicTableView: handleAdvancedSearch called with:', { filters, filterString, explicitSearchText });

        isHandlingAdvancedSearchRef.current = true;
        setCurrentSearchFilter(filterString || undefined);
        // If combining is disabled, clear any active simple search so users don't end up with multiple badges unknowingly.
        if (!combinedSearchesEnabledRef.current) {
            setCurrentSearchText(undefined);
            currentSearchTextRef.current = undefined;
            setCurrentSimpleDerivedFilter(undefined);
            currentSimpleDerivedFilterRef.current = undefined;
            setCurrentSearchQuery('');
            currentSearchQueryRef.current = '';
            setAppliedSimpleQuery('');
            appliedSimpleQueryRef.current = '';
            setAppliedOmitSearchDialectExpression(undefined);
            appliedOmitSearchDialectExpressionRef.current = undefined;
        }
        // Advanced search can still carry a simple query; keep badge aligned to the visible input.
        if (combinedSearchesEnabledRef.current) {
            setAppliedSimpleQuery((currentSearchQueryRef.current ?? '').trim());
        }
        if (explicitSearchAttributes !== undefined) {
            currentSearchAttributesRef.current = explicitSearchAttributes;
            setCurrentSearchAttributes(explicitSearchAttributes);
        }
        if (combinedSearchesEnabledRef.current && explicitSearchText !== undefined && explicitSearchText !== currentSearchTextRef.current) {
            setCurrentSearchText(explicitSearchText || undefined);
            currentSearchTextRef.current = explicitSearchText || undefined;
        }
        currentSearchFilterRef.current = filterString || undefined;

        loadPage(1).finally(() => {
            isHandlingAdvancedSearchRef.current = false;
        });

        updateUrlHashQueryParams({
            // Prefer the raw query currently shown in the input (so the badge/input doesn't "snap back" on hash parse).
            search: urlSearchQuery,
            sd: urlSearchQuery
                ? (appliedOmitSearchDialectExpressionRef.current === undefined
                    ? undefined
                    : (appliedOmitSearchDialectExpressionRef.current ? 'freetext' : 'expression'))
                : undefined,
            combine: urlSearchQuery
                ? (combinedSearchesEnabledRef.current ? true : undefined)
                : undefined,
            si: urlSearchQuery
                ? (getSearchInToStore(currentSearchAttributesRef.current) ?? undefined)
                : undefined,
            filter: filterString || '',
            page: 1,
        }, 'push');

        if (onAdvancedSearch) {
            onAdvancedSearch(filters);
        }
        if (isCustomSimpleFilter) {
            // Keep simple/simpleCustom independent; persist advanced filter and mark custom simple mode as last active.
            getUserSettingsService().setSearch(entityType, buildAdvancedSearchUpdates({ filter: filterString || undefined, lastSearchType: 'simpleCustom' }), true).catch(() => {});
        } else {
            getUserSettingsService().setSearch(entityType, buildAdvancedSearchUpdates({ filter: filterString || undefined, lastSearchType: 'advanced' }), true).catch(() => {});
        }
    }, [onAdvancedSearch, loadPage, entityType]);

    const handleExpressionDialectOmittedChange = useCallback((next: boolean) => {
        userInitiatedSearchRef.current = true;
        currentOmitSearchDialectExpressionRef.current = next;
        setOmitSearchDialectExpression(next);
    }, []);

    const handleCombinedSearchesEnabledChange = useCallback((next: boolean) => {
        userInitiatedSearchRef.current = true;
        setCombinedSearchesEnabled(next);
        combinedSearchesEnabledRef.current = next;
        // Persist immediately; non-blocking.
        getUserSettingsService().setSearch(entityType, { combined: next }, true).catch(() => {});
        // Do not update the URL immediately; URL is updated when a search/filter is actually applied.
    }, [entityType]);

    const handleSimpleDraftChange = useCallback((query: string) => {
        userInitiatedSearchRef.current = true;
        setDraftSimpleQuery(query);
        draftSimpleQueryRef.current = query;
        setCurrentSearchQuery(query);
        currentSearchQueryRef.current = query;
        onSearchQueryChange?.(query);
    }, [onSearchQueryChange]);

    const handleExpressionDraftChange = useCallback((query: string) => {
        userInitiatedSearchRef.current = true;
        setDraftExpressionQuery(query);
        draftExpressionQueryRef.current = query;
    }, []);

    const handleFreetextDraftChange = useCallback((query: string) => {
        userInitiatedSearchRef.current = true;
        setDraftFreetextQuery(query);
        draftFreetextQueryRef.current = query;
    }, []);

    const handleCustomDraftChange = useCallback((query: string) => {
        userInitiatedSearchRef.current = true;
        setDraftCustomQuery(query);
        draftCustomQueryRef.current = query;
    }, []);

    const handleSearchAttributesChange = useCallback((attrs: string[]) => {
        userInitiatedSearchRef.current = true;
        currentSearchAttributesRef.current = attrs;
        setCurrentSearchAttributes(attrs);
        // Do not update the URL or reset paging on Search In changes.
        // The URL is updated when a search/filter is actually applied (Enter / Apply / paging).
    }, []);

    const handleSimpleSearchModeChange = useCallback((mode: SimpleSearchType) => {
        userInitiatedSearchRef.current = true;
        setCurrentSimpleSearchType(mode);
        currentSimpleSearchTypeRef.current = mode;

        const nextDraft = (mode === 'simpleCustom' ? (draftCustomQueryRef.current ?? '') : (draftSimpleQueryRef.current ?? '')).trim();
        if (mode === 'simple' && nextDraft) {
            setCurrentSearchQuery(nextDraft);
            currentSearchQueryRef.current = nextDraft;
            return;
        }

        // If the buffer is empty, fall back to saved user settings for that mode (non-blocking).
        getUserSettingsService()
            .getSearch(entityType)
            .then((s) => {
                const fallback = (mode === 'simpleCustom' ? (s?.simpleCustom ?? '') : (s?.simple ?? '')).trim();
                if (!fallback) return;
                if (mode === 'simpleCustom') {
                    setDraftCustomQuery(fallback);
                    draftCustomQueryRef.current = fallback;
                    return;
                } else {
                    setDraftSimpleQuery(fallback);
                    draftSimpleQueryRef.current = fallback;
                }
                setCurrentSearchQuery(fallback);
                currentSearchQueryRef.current = fallback;
            })
            .catch(() => {});
    }, [entityType]);

    // When user opens Advanced, restore advanced-only saved searches and options.
    const handleAdvancedTabOpen = useCallback(() => {
        const keyEntityType = entityType;
        userSettingsFetchEntityTypeRef.current = keyEntityType;
        getUserSettingsService()
            .getSearch(entityType)
            .then((r) => {
                if (userSettingsFetchEntityTypeRef.current !== keyEntityType) return;
                const restored = getRestoredAdvancedQueryState(r);
                setAdvFilterForPhil(restored.filter);
                // Do not clobber drafts if the user already started typing after opening Advanced.
                if (!(draftExpressionQueryRef.current ?? '').trim()) {
                    setDraftExpressionQuery(restored.expressionQuery);
                    draftExpressionQueryRef.current = restored.expressionQuery;
                }
                if (!(draftFreetextQueryRef.current ?? '').trim()) {
                    setDraftFreetextQuery(restored.freetextQuery);
                    draftFreetextQueryRef.current = restored.freetextQuery;
                }
                if (restored.searchIn) {
                    setCurrentSearchAttributes(restored.searchIn);
                    currentSearchAttributesRef.current = restored.searchIn;
                }
                setCombinedSearchesEnabled(restored.combined);
                combinedSearchesEnabledRef.current = restored.combined;
            })
            .catch(() => {});
    }, [entityType]);

    // Track entity category for Address column
    const [isLocalityEntity, setIsLocalityEntity] = useState<boolean>(false);

    // Check if entity is a locality on mount
    useEffect(() => {
        const checkLocality = async () => {
            try {
                const { EntityCategorizationService } = await import('../../services/EntityCategorizationService');
                const categorizationService = EntityCategorizationService.getInstance();
                const categoryResult = await categorizationService.getEntityCategory(entityType);
                setIsLocalityEntity(categoryResult.type === 'locality');
            } catch (error) {
                console.error('Error checking entity category for Address column:', error);
                setIsLocalityEntity(false);
            }
        };
        checkLocality();
    }, [entityType]);

    // Map column key/title to API attribute id for entitlement filtering
    const getColumnAttributeId = useCallback((col: TableColumn<any>): string => {
        if (col.title) return col.title;
        const keyToAttr: Record<string, string> = {
            id: 'Id', displayname: 'DisplayName', type: 'EntityType', description: 'Description',
            address: 'Address', targettype: 'TargetType', attribute: 'Attribute', targetid: 'TargetId',
            recorded: 'Recorded', source: 'Source', status: 'Status'
        };
        return keyToAttr[col.key] ?? col.key;
    }, []);

    // Default column generation
    const defaultGenerateColumns = useCallback((): TableColumn<any>[] => {
        const lowerEntityType = entityType.toLowerCase();

        // Special handling for Journal Entry list columns (no sort; Id last).
        // Check for various forms: "JournalEntry", "Journal Entry", "journalentry", etc.
        const isJournalEntry = lowerEntityType === 'journalentry' ||
            lowerEntityType === 'journal entry' ||
            lowerEntityType.replace(/\s+/g, '') === 'journalentry';

        if (isJournalEntry) {
            debugLog('📋 DynamicTableView: Using Journal Entry column configuration for entityType:', entityType);
            const columns: TableColumn<any>[] = [];

            if (!localSelectedEntityType) {
                columns.push({
                    key: 'targettype',
                    label: 'Target Type',
                    title: 'TargetType',
                    sortable: false,
                    render: (entity: any) => entity.TargetType || entity.targetType || 'N/A'
                });
            }

            columns.push(
                {
                    key: 'displayname',
                    label: 'Display Name',
                    title: 'DisplayName',
                    sortable: false,
                    render: (entity: any) => {
                        const name = entity.DisplayName || entity.displayName || 'N/A';
                        return (
                            <Text size="sm" maw={360} truncate="end" title={name !== 'N/A' ? name : undefined}>
                                {name}
                            </Text>
                        );
                    }
                },
                {
                    key: 'attribute',
                    label: 'Attribute',
                    title: 'Attribute',
                    sortable: false,
                    render: (entity: any) => entity.Attribute || entity.attribute || 'N/A'
                },
                {
                    key: 'status',
                    label: 'Status',
                    title: 'Status',
                    sortable: false,
                    render: (entity: any) => {
                        const status = (entity.Status || entity.status || '').toString().trim();
                        if (!status) return '';
                        const colorMap: Record<string, string> = {
                            Completed: 'green', New: 'blue', Pending: 'yellow',
                            Denied: 'red', Expired: 'gray'
                        };
                        const color = colorMap[status] ?? 'gray';
                        return <Badge variant="light" size="sm" color={color}>{status}</Badge>;
                    }
                },
                {
                    key: 'targetid',
                    label: 'Target Id',
                    title: 'TargetId',
                    sortable: false,
                    render: (entity: any) => entity.TargetId || entity.targetId || 'N/A'
                },
                {
                    key: 'recorded',
                    label: 'Recorded',
                    title: 'Recorded',
                    sortable: false,
                    render: (entity: any) => {
                        const recorded = entity.Recorded || entity.recorded;
                        if (!recorded) return 'N/A';
                        if (typeof recorded === 'string' && isIsoDateTime(recorded)) {
                            const formatted = formatDateTime(recorded);
                            return (
                                <span title={recorded}>
                                    {formatted}
                                </span>
                            );
                        }
                        return recorded;
                    }
                }
            );

            if (!isStandard) {
                columns.push({
                    key: 'source',
                    label: 'Source',
                    title: 'Source',
                    sortable: false,
                    render: (entity: any) => {
                        const source = (entity.Source || entity.source || '').toString().trim();
                        if (!source) return '';
                        const colorMap: Record<string, string> = { SCIM: 'cyan', Scorpius3: 'teal' };
                        const color = colorMap[source] ?? 'gray';
                        return <Badge variant="outline" size="sm" color={color}>{source}</Badge>;
                    }
                });
            }

            if (!isStandard) {
                columns.push({
                    key: 'id',
                    label: 'Id',
                    title: 'Id',
                    sortable: false,
                    render: (entity: any) => {
                        const id = entity.Id || entity.id || '';
                        const shortenId = (idValue: string): string => {
                            if (!idValue) return 'N/A';
                            if (idValue.length <= 12) return idValue;
                            return `${idValue.substring(0, 8)}...`;
                        };
                        const handleCopyId = async (event: React.MouseEvent) => {
                            event.stopPropagation();
                            await copyToClipboard(id);
                        };
                        return (
                            <Group gap={4} align="center">
                                <Text
                                    size="sm"
                                    ff="monospace"
                                    title={id || 'N/A'}
                                >
                                    {shortenId(id)}
                                </Text>
                                {id && (
                                    <Tooltip label="Copy ID to clipboard">
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            onClick={handleCopyId}
                                        >
                                            <IconCopy size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                            </Group>
                        );
                    }
                });
            }

            if (Array.isArray(allowedTableAttributeIds)) {
                const allowedLower = new Set(allowedTableAttributeIds.map((a) => a.toLowerCase()));
                const alwaysShow = new Set(['id', 'displayname']);
                return columns.filter(c => {
                    const attr = getColumnAttributeId(c).toLowerCase();
                    return alwaysShow.has(attr) || allowedLower.has(attr);
                });
            }
            return columns;
        }

        // Standard column generation for other entity types
        const columns: TableColumn<any>[] = [
            {
                key: 'displayname',
                label: 'Display Name',
                title: 'DisplayName',
                sortable: true,
                render: (entity: any, index: number) => (
                    <DisplayNameCell
                        displayName={entity.DisplayName || entity.Name || entity.displayName || entity.name}
                        id={entity.Id || entity.id}
                        index={index}
                        startIndex={(currentPage - 1) * itemsPerPage}
                    />
                )
            }
        ];

        // Add Address column for locality entities
        if (isLocalityEntity) {
            columns.push({
                key: 'address',
                label: 'Address',
                sortable: true,
                render: (entity: any) => entity.Address || entity.address || 'N/A'
            });
        }

        // Only show Type column for "Person" entity type (All Users view)
        if (lowerEntityType === 'person') {
            columns.push({
                key: 'type',
                label: 'Type',
                sortable: true,
                render: (entity: any) => entity.EntityType || entity.Type || entity.type || 'N/A'
            });
        }

        // Add Description column for schema entities
        if (lowerEntityType === 'schemaentity') {
            columns.push({
                key: 'description',
                label: 'Description',
                sortable: true,
                render: (entity: any) => entity.Description || entity.description || 'N/A'
            });
        }

        columns.push({
            key: 'id',
            label: 'Id',
            sortable: true,
            render: (entity: any) => entity.Id || entity.id || 'N/A'
        });

        if (Array.isArray(allowedTableAttributeIds)) {
            const allowedLower = new Set(allowedTableAttributeIds.map((a) => a.toLowerCase()));
            const alwaysShow = new Set(['id', 'displayname']);
            return columns.filter(c => {
                const attr = getColumnAttributeId(c).toLowerCase();
                return alwaysShow.has(attr) || allowedLower.has(attr);
            });
        }
        return columns;
    }, [entityType, isLocalityEntity, currentPage, itemsPerPage, allowedTableAttributeIds, getColumnAttributeId, isStandard]);

    // Generate table columns (filter out Id in standard mode, except Person -- help desk needs IDs)
    const columns = useMemo(() => {
        const raw = generateColumns ? generateColumns() : defaultGenerateColumns();
        if (isStandard && !isPerson(entityType) && entityType.toLowerCase() !== 'person') {
            return raw.filter(c => c.key.toLowerCase() !== 'id');
        }
        return raw;
    }, [generateColumns, defaultGenerateColumns, localSelectedEntityType, isStandard, entityType]);

    // Actions are now handled in detail view, not in table
    const actions: TableAction<any>[] = [];

    // Create dropdown content for custom loadData functions
    const createDropdownContent = () => {
        const normalizedEntityType = entityType.toLowerCase();
        const isSchemaEntity = normalizedEntityType === 'schemaentity';
        const isJournalEntry = normalizedEntityType === 'journalentry' ||
            normalizedEntityType === 'journal entry' ||
            normalizedEntityType.replace(/\s+/g, '') === 'journalentry';

        // Show dropdown for schemaentity (with custom loadData) or journalentry (using defaultLoadData)
        if (isSchemaEntity && !loadData) {
            return null;
        }
        if (!isSchemaEntity && !isJournalEntry) {
            return null;
        }

        const isForJournalEntry = isJournalEntry;

        const selectData = [
            { value: '', label: isForJournalEntry ? 'All target types...' : 'Select an entity type...' },
            ...entityTypes
                .filter((type: { name: string }) => {
                    if (isForJournalEntry) {
                        return true;
                    }
                    return type.name.toLowerCase() !== 'schemaentity' && type.name.toLowerCase() !== 'schemaattribute';
                })
                .map((type: { name: string }) => ({
                    value: type.name.toLowerCase(),
                    label: isForJournalEntry ? getPluralizedLabel(type.name) : getSingularLabel(type.name)
                }))
        ];

        return (
            <Paper withBorder p="md" mb="md">
                <Title order={6} mb="sm">
                    {isForJournalEntry ? 'Filter by Target Type' : 'Select Entity Type'}
                </Title>
                <Select
                    label={isForJournalEntry ? 'Target Type' : 'Entity Type'}
                    placeholder={isForJournalEntry ? 'All target types...' : 'Select an entity type...'}
                    value={localSelectedEntityType}
                    onChange={(value) => setLocalSelectedEntityType(value ?? '')}
                    data={selectData}
                    clearable
                    maw={300}
                />
                {!localSelectedEntityType && (
                    <Text size="sm" c="dimmed" mt="xs">
                        {isForJournalEntry
                            ? 'Select a target type to filter journal entries, or leave empty to view all entries.'
                            : 'Please select an entity type to view its schema data.'}
                    </Text>
                )}
            </Paper>
        );
    };

    // Check if this is a journal entry entity type (case-insensitive)
    const isJournalEntry = useMemo(() => {
        const normalizedType = entityType.toLowerCase();
        return normalizedType === 'journalentry';
    }, [entityType]);

    // Close create dialog when entityType changes to prevent state conflicts
    useEffect(() => {
        setCreateDialogOpen(false);
    }, [entityType]);

    const createActionButtons = () => {
        if (hideToolbarActions) return null;
        const shouldShowDefaultCreate = availableActions.length === 0 && !isJournalEntry;
        const hasRegisteredActions = availableActions.length > 0;

        if (!shouldShowDefaultCreate && !hasRegisteredActions && !bulkDeleteEnabled) {
            return null;
        }

        const entityLabel = getSingularLabel(entityType);

        return (
            <>
                {hasRegisteredActions && availableActions.map((action) => (
                    <Button
                        key={action.id}
                        variant="filled"
                        leftSection={<IconPlus size={16} />}
                        onClick={() => onInlineCreate ? onInlineCreate() : executeAction(action.id)}
                        size="xs"
                    >
                        {action.label}
                    </Button>
                ))}
                {shouldShowDefaultCreate && (
                    <Tooltip label={!showCreateButton ? "You don't have permission to create this entity type." : undefined}>
                        <Box component="span" display="inline-block">
                            <Button
                                variant="filled"
                                color="blue"
                                leftSection={<IconPlus size={16} />}
                                onClick={() => onInlineCreate ? onInlineCreate() : setCreateDialogOpen(true)}
                                size="xs"
                                disabled={!showCreateButton}
                            >
                                Create {entityLabel}
                            </Button>
                        </Box>
                    </Tooltip>
                )}
                {bulkDeleteEnabled && (
                    <Tooltip label={selectedForDelete.length === 0 ? 'Select one or more rows to delete' : undefined}>
                        <Box component="span" display="inline-block">
                            <Button
                                color="red"
                                variant="light"
                                leftSection={<IconTrash size={16} />}
                                size="xs"
                                disabled={selectedForDelete.length === 0}
                                onClick={() => {
                                    setBulkDeleteError(null);
                                    setBulkDeleteDialogOpen(true);
                                }}
                            >
                                Delete ({selectedForDelete.length})
                            </Button>
                        </Box>
                    </Tooltip>
                )}
                {entityConfig?.tableFlags?.showSystemFilter && (
                    <Switch
                        checked={showSystemSets}
                        onChange={(e) => {
                            const newValue = e.currentTarget.checked;
                            setShowSystemSets(newValue);
                            showSystemSetsRef.current = newValue;
                            loadPage(1);
                        }}
                        size="sm"
                        label="Show system sets"
                    />
                )}
            </>
        );
    };

    // Handle action success
    const handleActionSuccess = useCallback(() => {
        // Refresh the entity list after successful action - hook will use current search state via refs
        reload();
    }, [reload]);

    // Memoize create dialog handlers to prevent infinite re-renders
    const handleCreateDialogClose = useCallback(async () => {
        await onCloseCreateDialog?.();
        setCreateDialogOpen(false);
    }, [onCloseCreateDialog]);

    const handleCreateDialogSuccess = useCallback(() => {
        setCreateDialogOpen(false);
        handleActionSuccess();
    }, [handleActionSuccess]);

    // Get entity icon component (centralized so nav and header match)
    const getEntityIconComponent = (entityType: string) => {
        const Icon = getEntityIcon(entityType);
        return <Icon />;
    };

    if (loading && entities.length === 0 && !(entityType.toLowerCase() === 'schemaentity' && !selectedEntityType)) {
        return <LoadingState variant="skeleton" rows={10} minHeight={400} />;
    }

    // Debug logging for render
    // Reduced logging - removed render-time spam

    return (
        <Box>
            {(() => {
                if (hideHeader) return null;
                const lower = entityType.toLowerCase();
                const hideForSchema = lower === 'schemaentity' || lower === 'schemabinding';
                if (hideForSchema) return null;
                const isAdvanced = viewMode === 'advanced';
                return (
                    <Group justify="space-between" align="center" mb="sm" wrap="wrap" className="hades-mgmt-toolbar">
                        <Group gap="sm" align="center" wrap="nowrap">
                            <Title order={4}>
                                <Group gap="xs" align="center" wrap="nowrap">
                                    {getEntityIconComponent(entityType)}
                                    {getSingularLabel(entityType)} Management{isDisjointed ? (
                                        <Tooltip label="Disjointed type"><span> *</span></Tooltip>
                                    ) : ''}
                                </Group>
                            </Title>
                            {customHeaderContent}
                        </Group>
                        <Group gap="xs" align="center" wrap="nowrap">
                            {createActionButtons()}
                            <Tooltip label={isAdvanced ? 'Switch to Standard view' : 'Switch to Admin view'} position="bottom" withArrow>
                                <Button
                                    onClick={toggleViewMode}
                                    variant={isAdvanced ? 'filled' : 'light'}
                                    color={isAdvanced ? 'orange' : 'gray'}
                                    size="compact-sm"
                                    leftSection={isAdvanced ? <IconLayoutList size={14} /> : <IconLayoutDashboard size={14} />}
                                    fw={600}
                                >
                                    {isAdvanced ? 'Admin' : 'Standard'}
                                </Button>
                            </Tooltip>
                        </Group>
                    </Group>
                );
            })()}

            {tier2Error && (
                <Alert color="red" mb="md" title="Entitlements error">
                    {tier2Error}
                </Alert>
            )}

            {error && (
                <Alert color="red" mb="md">
                    {error}
                </Alert>
            )}

            {userSettingsError && (
                <Group gap="xs" align="center" mb="xs" wrap="nowrap">
                    <Text size="xs" c="orange.7" flex={1}>{userSettingsError}</Text>
                    <ActionIcon size="sm" variant="subtle" color="orange" aria-label="Dismiss" onClick={() => setUserSettingsError(null)}>
                        <IconX size={14} />
                    </ActionIcon>
                </Group>
            )}

            {tabsMode === 'full' ? (
                <EntityTableWithTabs<any>
                    error={searchError}
                    entityType={entityType}
                    isDisjointed={isDisjointed}
                    onAdvancedTabOpen={handleAdvancedTabOpen}
                    initialAdvFilter={advFilterForPhil}
                    data={entities}
                    isLoading={loading}
                    currentPage={currentPage}
                    totalResults={totalResults}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    columns={columns}
                    getRowKey={(entity) => {
                        return getEntityId(entity);
                    }}
                    onRowClick={(entity) => {
                        // Pass the full entity directly to onEntitySelect if available, otherwise find it by ID
                        if (onEntitySelect) {
                            onEntitySelect(entity);
                        } else {
                            const entityId = entity.Id || entity.id;
                            const idString = typeof entityId === 'string' ? entityId :
                                (entityId && typeof entityId === 'object' && entityId.value) ? entityId.value :
                                    (entityId && typeof entityId === 'object' && entityId.Id) ? entityId.Id :
                                        String(entityId);
                            handleEntitySelect(idString);
                        }
                    }}
                    selectedEntityId={selectedEntityId}
                    deepLinkEntityType={deepLinkEntityType}
                    onEntitySelect={(entity) => {
                        const entityToPass = (entity?.rawData && typeof entity.rawData === 'object' && !Array.isArray(entity.rawData))
                            ? entity.rawData
                            : entity;
                        handleEntitySelect(entityToPass);
                    }}
                    onRefresh={reload}
                    entityService={{
                        getEntityById: async (id: string) => {
                            return {
                                Id: id || '',
                                Name: '',
                                Type: entityType,
                                Actions: [],
                                rawData: { Id: id, id }
                            };
                        }
                    }}
                    onSearch={handleSimpleSearch}
                    onAdvancedSearch={handleAdvancedSearch}
                    appliedAdvancedFilter={currentSearchFilter}
                    onClearAppliedFilter={currentSearchFilter ? () => {
                        setCurrentSearchFilter(undefined);
                        currentSearchFilterRef.current = undefined;
                        loadPage(1);
                    } : undefined}
                    appliedSearchQuery={appliedSimpleQueryForUi}
                    appliedDerivedFilter={currentSimpleDerivedFilter}
                    appliedSearchText={currentSearchText}
                    initialSearchQuery={appliedSimpleQuery}
                    simpleSearchQuery={draftSimpleQuery}
                    expressionSearchQuery={draftExpressionQuery}
                    freetextSearchQuery={draftFreetextQuery}
                    customSearchQuery={draftCustomQuery}
                    onSimpleSearchQueryChange={handleSimpleDraftChange}
                    onExpressionSearchQueryChange={handleExpressionDraftChange}
                    onFreetextSearchQueryChange={handleFreetextDraftChange}
                    onCustomSearchQueryChange={handleCustomDraftChange}
                    searchAttributes={currentSearchAttributes}
                    onSearchAttributesChange={handleSearchAttributesChange}
                    expressionDialectOmitted={omitSearchDialectExpression}
                    onExpressionDialectOmittedChange={handleExpressionDialectOmittedChange}
                    appliedExpressionDialectOmitted={appliedOmitSearchDialectExpression}
                    combinedSearchesEnabled={combinedSearchesEnabled}
                    onCombinedSearchesEnabledChange={handleCombinedSearchesEnabledChange}
                    initialSimpleSearchMode={currentSimpleSearchType}
                    onSimpleSearchModeChange={handleSimpleSearchModeChange}
                    appliedSimpleSearchType={currentSimpleSearchType}
                    rowErrors={effectiveRowErrors}
                    onClearRowError={handleClearEffectiveRowError}
                    sortState={sortingDisabledForEntity ? null : sortState}
                    onSortChange={sortingDisabledForEntity ? undefined : handleSortChange}
                    listResponse={listResponse}
                    allowedSearchAttributeIds={tier2SearchAttributeIds !== null ? tier2SearchAttributeIds : allowedSearchAttributeIds}
                    syncUrlToHash={false}
                    showEditIcon={!hideToolbarActions && canEditRow && !!onEditRowClick}
                    onEditClick={!hideToolbarActions ? onEditRowClick : undefined}
                    selectable={!hideToolbarActions && !isStandard && bulkDeleteEnabled}
                    selectedItems={!hideToolbarActions && !isStandard && bulkDeleteEnabled ? selectedForDelete : undefined}
                    onSelectionChange={!hideToolbarActions && !isStandard && bulkDeleteEnabled ? setSelectedForDelete : undefined}
                    customHeaderContent={createDropdownContent() || undefined}
                    bindingConstraintsMap={tableBindingConstraintsMap}
                    hideAdminTabs={isStandard}
                    hideSearchExtras={isStandard}
                    hideTopPagination={isStandard}
                    hideDebugTabs={isStandard}
                    customDataRenderer={customDataRenderer}
                />
            ) : (
                <GenericSearch<any>
                    entityType={entityType}
                    data={entities}
                    isLoading={loading}
                    error={searchError}
                    currentPage={currentPage}
                    totalResults={totalResults}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    columns={columns}
                    getRowKey={(entity: any) => getEntityId(entity)}
                    onRowClick={(entity: any) => {
                        if (onEntitySelect) onEntitySelect(entity);
                    }}
                    onSearch={handleSimpleSearch}
                    onAdvancedSearch={handleAdvancedSearch}
                    appliedAdvancedFilter={currentSearchFilter}
                    appliedSearchQuery={appliedSimpleQueryForUi}
                    appliedDerivedFilter={currentSimpleDerivedFilter}
                    appliedSearchText={currentSearchText}
                    initialSearchQuery={appliedSimpleQuery}
                    simpleSearchQuery={draftSimpleQuery}
                    expressionSearchQuery={draftExpressionQuery}
                    freetextSearchQuery={draftFreetextQuery}
                    customSearchQuery={draftCustomQuery}
                    onSimpleSearchQueryChange={handleSimpleDraftChange}
                    onExpressionSearchQueryChange={handleExpressionDraftChange}
                    onFreetextSearchQueryChange={handleFreetextDraftChange}
                    onCustomSearchQueryChange={handleCustomDraftChange}
                    searchAttributes={currentSearchAttributes}
                    onSearchAttributesChange={handleSearchAttributesChange}
                    expressionDialectOmitted={omitSearchDialectExpression}
                    onExpressionDialectOmittedChange={handleExpressionDialectOmittedChange}
                    appliedExpressionDialectOmitted={appliedOmitSearchDialectExpression}
                    combinedSearchesEnabled={combinedSearchesEnabled}
                    onCombinedSearchesEnabledChange={handleCombinedSearchesEnabledChange}
                    initialSimpleSearchMode={currentSimpleSearchType}
                    onSimpleSearchModeChange={handleSimpleSearchModeChange}
                    appliedSimpleSearchType={currentSimpleSearchType}
                    onRefresh={reload}
                    listResponse={listResponse}
                    allowedSearchAttributeIds={tier2SearchAttributeIds !== null ? tier2SearchAttributeIds : allowedSearchAttributeIds}
                    syncUrlToHash={false}
                    tableVariant="dataOnly"
                    customDataRenderer={customDataRenderer}
                />
            )}

            {!hideToolbarActions && bulkDeleteEnabled && (
                <ShadcnDeleteConfirmDialog
                    open={bulkDeleteDialogOpen}
                    onClose={() => {
                        if (isBulkDeleting) return;
                        setBulkDeleteDialogOpen(false);
                    }}
                    onConfirm={handleConfirmBulkDelete}
                    entityType={entityType}
                    entityName=""
                    isDeleting={isBulkDeleting}
                    error={bulkDeleteError}
                    title={`Delete ${entityType}`}
                    confirmMessage={`Are you sure you want to delete ${selectedForDelete.length} ${entityType.toLowerCase()} item(s)?`}
                />
            )}

            {/* Render action components */}
            {actionComponent && (
                <Box>
                    {React.cloneElement(actionComponent, {
                        onSuccess: handleActionSuccess,
                        onClose: closeAction
                    })}
                </Box>
            )}

            {/* Default create dialog for entity types without registered actions */}
            {/* Only render when dialog should be open to prevent unnecessary effects */}
            {!isJournalEntry && createDialogOpen && (
                <GenericEntityUpsertDialog
                    key={`create-dialog-${entityType}`}
                    open={createDialogOpen}
                    onClose={handleCreateDialogClose}
                    onSuccess={handleCreateDialogSuccess}
                    entityType={entityType}
                />
            )}
        </Box>
    );
};
